import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout, { PageHeader } from "@/components/Layout";
import api from "@/lib/api";
import { BookMarked, ShieldCheck, ArrowRight, RotateCw, TriangleAlert } from "lucide-react";

const V_STYLE = { Draft: "bg-amber-100 text-amber-800 border-amber-300", Published: "bg-green-100 text-green-700 border-green-300", Superseded: "bg-slate-100 text-slate-500 border-slate-300" };

export default function Methodology() {
  const [versions, setVersions] = useState(null);
  const [error, setError] = useState(null);

  // Whether a methodology exists is a real gate condition, so a failed request must never render
  // as "there is no published version". Loading, failure and genuine emptiness are three states.
  const load = useCallback(() => {
    setError(null);
    setVersions(null);
    api.get("/program-library/versions")
      .then((r) => setVersions(r.data))
      .catch((e) => setError(e?.response?.data?.detail || e?.message || "Gagal memuat versi metodologi"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = versions || [];
  const published = rows.filter((v) => v.status === "Published");
  const active = published[0] || rows[0];
  const activeLabel = error ? "Tidak dapat dimuat" : versions === null ? "Memuat..." : active ? active.methodology_version : "Belum ada versi terpublikasi";

  return (
    <Layout>
      <PageHeader breadcrumb="Metodologi" title="Standar & Metodologi" subtitle="Kerangka standar audit & versi metodologi yang mengendalikan seluruh perikatan" />
      <div className="p-6 space-y-6" data-testid="methodology-page">
        <div className="bg-white border border-border rounded-sm p-5">
          <div className="flex items-center gap-2 mb-3"><ShieldCheck size={18} className="text-slate-700" /><span className="font-head font-semibold">Kerangka yang Berlaku</span></div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground">Standar Audit</div><div className="text-sm font-medium mt-0.5">SA / SPAP (IAPI)</div></div>
            <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground">Kerangka Pelaporan</div><div className="text-sm font-medium mt-0.5">SAK / SAK ETAP</div></div>
            <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground">Versi Metodologi Aktif</div><div className="text-sm font-medium mt-0.5" data-testid="method-active-version">{activeLabel}</div></div>
          </div>
          <p className="text-xs text-muted-foreground mt-4 max-w-3xl">
            Setiap perikatan dikunci ke satu versi metodologi saat pertama kali diinisiasi. Versi lama tidak berubah secara diam-diam ketika versi baru dipublikasikan — memastikan keterlacakan dan konsistensi metodologi antar tahun.
          </p>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-head font-semibold uppercase text-muted-foreground">Riwayat Versi Metodologi</div>
            <Link to="/program-library" className="text-xs text-slate-700 hover:underline flex items-center gap-1" data-testid="method-to-library">Kelola Pustaka Program <ArrowRight size={13} /></Link>
          </div>
          <div className="bg-white border border-border rounded-sm overflow-x-auto">
            <table className="w-full audit-table">
              <thead><tr><th>Versi Metodologi</th><th>Status</th><th>Jumlah Prosedur</th><th>Dibuat Oleh</th></tr></thead>
              <tbody>
                {rows.map((v) => (
                  <tr key={v.id} data-testid={`method-version-${v.id}`}>
                    <td className="text-sm font-medium flex items-center gap-1"><BookMarked size={13} />{v.methodology_version}</td>
                    <td><span className={`text-[10px] px-1.5 py-0.5 border rounded-sm ${V_STYLE[v.status]}`}>{v.status}</span></td>
                    <td className="tabular">{v.template_count}</td>
                    <td className="text-xs">{v.created_by}</td>
                  </tr>
                ))}
                {versions === null && !error && <tr><td colSpan={4} className="text-center text-muted-foreground py-6" data-testid="method-loading">Memuat...</td></tr>}
                {error && (
                  <tr><td colSpan={4} className="py-6" data-testid="method-error">
                    <div className="flex items-center justify-center gap-2 text-red-700 font-medium"><TriangleAlert size={15} /> Daftar versi metodologi tidak dapat dimuat</div>
                    <div className="text-center text-xs text-muted-foreground mt-1">{error}</div>
                    <div className="text-center text-[11px] text-muted-foreground mt-1">Ini bukan berarti belum ada metodologi — daftar tidak berhasil diambil.</div>
                    <div className="text-center mt-3"><button onClick={load} data-testid="method-retry" className="inline-flex items-center gap-1.5 text-xs font-medium border border-border rounded-sm px-3 py-1.5 hover:border-slate-800"><RotateCw size={13} /> Coba lagi</button></div>
                  </td></tr>
                )}
                {versions !== null && rows.length === 0 && !error && <tr><td colSpan={4} className="text-center text-muted-foreground py-6" data-testid="method-empty">Belum ada versi metodologi. Buat di Pustaka Program.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
